import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useWsStore } from "./ws";

const ISSUER = import.meta.env.VITE_ZITADEL_ISSUER ?? "https://auth.mtp.group";
const CLIENT_ID = import.meta.env.VITE_ZITADEL_CLIENT_ID ?? "";
const rawRedirect =
	import.meta.env.VITE_ZITADEL_REDIRECT_URI ?? "/auth/callback";
const REDIRECT_URI = rawRedirect.startsWith("http")
	? rawRedirect
	: `${window.location.origin}${rawRedirect}`;

/** Refresh 60 seconds before the token actually expires. */
const REFRESH_MARGIN_MS = 60_000;

interface User {
	id: string;
	name: string;
	email: string;
	isSuperAdmin: boolean;
}

interface AuthState {
	token: string | null;
	refreshToken: string | null;
	user: User | null;
	isAuthenticated: boolean;
	login: () => void;
	handleCallback: (code: string) => Promise<void>;
	logout: () => void;
	setToken: (token: string) => void;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
	try {
		const parts = token.split(".");
		const payload = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
		return JSON.parse(payload);
	} catch {
		return {};
	}
}

function getTokenExpMs(token: string): number | null {
	const payload = decodeJwtPayload(token);
	if (typeof payload.exp === "number") {
		return payload.exp * 1000;
	}
	return null;
}

function userFromTokens(accessToken: string, idToken?: string): User {
	const access = decodeJwtPayload(accessToken);
	const id = idToken ? decodeJwtPayload(idToken) : access;

	// Roles are in access_token (access_token_role_assertion)
	const roles = access["urn:zitadel:iam:org:project:roles"] as
		| Record<string, unknown>
		| undefined;

	// Profile info is in id_token (id_token_userinfo_assertion)
	return {
		id: (id.sub as string) ?? (access.sub as string) ?? "",
		name:
			(id.name as string) ??
			(id.preferred_username as string) ??
			(access.name as string) ??
			"User",
		email: (id.email as string) ?? (access.email as string) ?? "",
		isSuperAdmin: roles ? "super_admin" in roles : false,
	};
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function clearRefreshTimer() {
	if (refreshTimer != null) {
		clearTimeout(refreshTimer);
		refreshTimer = null;
	}
}

function scheduleRefresh(accessToken: string) {
	clearRefreshTimer();
	const expMs = getTokenExpMs(accessToken);
	if (expMs == null) return;

	const delay = expMs - Date.now() - REFRESH_MARGIN_MS;
	if (delay <= 0) {
		// Already past the refresh window — refresh immediately
		void silentRefresh();
		return;
	}

	refreshTimer = setTimeout(() => {
		void silentRefresh();
	}, delay);
}

async function exchangeRefreshToken(rt: string): Promise<{
	access_token: string;
	refresh_token?: string;
	id_token?: string;
} | null> {
	try {
		const resp = await fetch(`${ISSUER}/oauth/v2/token`, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
			body: new URLSearchParams({
				grant_type: "refresh_token",
				client_id: CLIENT_ID,
				refresh_token: rt,
			}),
		});
		if (!resp.ok) return null;
		return await resp.json();
	} catch {
		return null;
	}
}

async function silentRefresh(): Promise<boolean> {
	const { refreshToken, logout } = useAuthStore.getState();
	if (!refreshToken) {
		logout();
		return false;
	}

	const data = await exchangeRefreshToken(refreshToken);
	if (!data) {
		logout();
		return false;
	}

	const token = data.access_token;
	const newRefreshToken = data.refresh_token ?? refreshToken;
	const idToken = data.id_token;
	const user = userFromTokens(token, idToken);

	useAuthStore.setState({
		token,
		refreshToken: newRefreshToken,
		user,
		isAuthenticated: true,
	});

	scheduleRefresh(token);
	return true;
}

/** Attempt a silent refresh — used by the gRPC interceptor on auth failures. */
export { silentRefresh };

export const useAuthStore = create<AuthState>()(
	persist(
		(set) => ({
			token: null,
			refreshToken: null,
			user: null,
			isAuthenticated: false,

			login: () => {
				// Save current URL to return to after login
				const currentPath = window.location.pathname + window.location.search;
				if (currentPath !== "/" && currentPath !== "/auth/callback") {
					localStorage.setItem("auth_return_to", currentPath);
				}

				const params = new URLSearchParams({
					client_id: CLIENT_ID,
					redirect_uri: REDIRECT_URI,
					response_type: "code",
					scope:
						"openid email profile offline_access " +
						"urn:zitadel:iam:org:id " +
						"urn:zitadel:iam:org:project:id:zitadel:aud " +
						"urn:zitadel:iam:org:projects:roles",
					code_challenge_method: "S256",
				});

				const verifier = crypto.randomUUID() + crypto.randomUUID();
				localStorage.setItem("pkce_verifier", verifier);

				crypto.subtle
					.digest("SHA-256", new TextEncoder().encode(verifier))
					.then((hash) => {
						const challenge = btoa(String.fromCharCode(...new Uint8Array(hash)))
							.replace(/\+/g, "-")
							.replace(/\//g, "_")
							.replace(/=+$/, "");

						params.set("code_challenge", challenge);

						window.location.href = `${ISSUER}/oauth/v2/authorize?${params}`;
					});
			},

			handleCallback: async (code: string) => {
				const verifier = localStorage.getItem("pkce_verifier") ?? "";

				if (!verifier) {
					throw new Error(
						"PKCE verifier missing – please try logging in again",
					);
				}

				const resp = await fetch(`${ISSUER}/oauth/v2/token`, {
					method: "POST",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: new URLSearchParams({
						grant_type: "authorization_code",
						code,
						client_id: CLIENT_ID,
						redirect_uri: REDIRECT_URI,
						code_verifier: verifier,
					}),
				});

				if (!resp.ok) {
					const body = await resp.text().catch(() => "");
					throw new Error(`Token exchange failed: ${resp.status} ${body}`);
				}

				localStorage.removeItem("pkce_verifier");

				const data = await resp.json();
				const token = data.access_token as string;
				const refreshToken = (data.refresh_token as string) ?? null;
				const idToken = data.id_token as string | undefined;
				const user = userFromTokens(token, idToken);

				set({
					token,
					refreshToken,
					user,
					isAuthenticated: true,
				});

				scheduleRefresh(token);
			},

			logout: () => {
				clearRefreshTimer();
				useWsStore.getState().disconnect();
				set({
					token: null,
					refreshToken: null,
					user: null,
					isAuthenticated: false,
				});
				window.location.href =
					`${ISSUER}/oidc/v1/end_session?` +
					`post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}&` +
					`client_id=${CLIENT_ID}`;
			},

			setToken: (token: string) => {
				const user = userFromTokens(token);
				set({
					token,
					user,
					isAuthenticated: true,
				});
				scheduleRefresh(token);
			},
		}),
		{
			name: "omnios-auth",
			partialize: (state) => ({
				token: state.token,
				refreshToken: state.refreshToken,
				user: state.user,
				isAuthenticated: state.isAuthenticated,
			}),
		},
	),
);

// Schedule refresh for token restored from localStorage on page load.
const restoredToken = useAuthStore.getState().token;
if (restoredToken) {
	scheduleRefresh(restoredToken);
}
